import React from 'react';
import { arrayOf, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { LinkedLogo, PhoneNumber } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';

const phoneNumber = process.env.REACT_APP_TEAM_PHONE_NUMBER;
const teamEmail = process.env.REACT_APP_TEAM_EMAIL;
const appStoreUrl = process.env.REACT_APP_APP_STORE_URL;
const googlePlayUrl = process.env.REACT_APP_GOOGLE_PLAY_URL;

const GRID_CSS = [css.gridCol1, css.gridCol2, css.gridCol3, css.gridCol4];

const getGridCss = numberOfColumns => GRID_CSS[numberOfColumns - 1] || GRID_CSS[0];

// Parses {{word}} tokens into highlight spans, matching the hero headline style.
// Falls back to auto-highlighting the branded words "Anything" and "Anyone".
const SloganRenderer = ({ slogan, className }) => {
  const content = slogan?.content;
  if (!content) return null;

  const HIGHLIGHT_TOKEN = /\{\{(.*?)\}\}/g;
  const hasBraces = HIGHLIGHT_TOKEN.test(content);
  const raw = hasBraces ? content : content.replace(/\b(Anything|Anyone)\b/g, '{{$1}}');

  const segments = [];
  let lastIndex = 0;
  const pattern = /\{\{(.*?)\}\}/g;
  let match = pattern.exec(raw);
  while (match) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: raw.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'highlight', value: match[1] });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(raw);
  }
  if (lastIndex < raw.length) {
    segments.push({ type: 'text', value: raw.slice(lastIndex) });
  }

  return (
    <p className={className}>
      {segments.map((seg, i) =>
        seg.type === 'highlight' ? (
          <span key={i} className={css.sloganHighlight}>{seg.value}</span>
        ) : (
          <span key={i}>{seg.value}</span>
        )
      )}
    </p>
  );
};

const AppleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden="true" focusable="false">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83zM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const GooglePlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden="true" focusable="false">
    <path d="M3.18 23.76c.32.18.69.19 1.04.02l13.2-7.4-2.82-2.83-11.42 10.21zm-1.1-20.7C2.03 3.28 2 3.56 2 3.84v16.32c0 .28.03.56.08.82l11.75-10.5L2.08 3.06zm14.9 8.54l-2.58-2.58L3.22.27C2.87.1 2.5.1 2.18.28l11.7 10.45 3.1-3.13zM21.4 10.6l-3.07-1.72-3.01 3.01 3.01 3.02 3.1-1.74c.88-.5.88-2.08-.03-2.57z" />
  </svg>
);

const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns,
    socialMediaLinks,
    slogan,
    appearance,
    copyright,
    blocks,
    options,
    linkLogoToExternalSite,
  } = props;

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const linksWithBlockId = socialMediaLinks?.map(sml => ({
    ...sml,
    blockId: sml.link.platform,
  }));

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const showAppButtons = appStoreUrl || googlePlayUrl;

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>

        {/* Header: logo (left) + app store buttons (right) */}
        <div className={css.footerHeader}>
          <div className={css.logoArea}>
            <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout="desktop"
            />
            <div className={css.sloganMobile}>
              <SloganRenderer slogan={slogan} className={css.slogan} />
            </div>
          </div>

          {showAppButtons && (
            <div className={css.appButtons}>
              {appStoreUrl && (
                <a
                  href={appStoreUrl}
                  className={css.appButton}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                >
                  <AppleIcon />
                  <span className={css.appButtonText}>
                    <span className={css.appButtonSubtitle}>DOWNLOAD ON THE</span>
                    <span className={css.appButtonTitle}>App Store</span>
                  </span>
                </a>
              )}
              {googlePlayUrl && (
                <a
                  href={googlePlayUrl}
                  className={css.appButton}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                >
                  <GooglePlayIcon />
                  <span className={css.appButtonText}>
                    <span className={css.appButtonSubtitle}>GET IT ON</span>
                    <span className={css.appButtonTitle}>Google Play</span>
                  </span>
                </a>
              )}
            </div>
          )}
        </div>

        <hr className={css.divider} />

        {/* Main columns: contact (desktop first / mobile last) + CMS content blocks */}
        <div className={css.mainColumns}>

          {/* Contact column */}
          <div className={css.contactColumn}>
            <span className={css.columnHeading}>CONTACT</span>
            <div className={css.contactLinks}>
              <PhoneNumber phoneNumber={phoneNumber} className={css.phoneLink} />
              {teamEmail && (
                <>
                  <span className={css.bulletSeparator} aria-hidden="true"> • </span>
                  <a href={`mailto:${teamEmail}`} className={css.emailLink}>{teamEmail}</a>
                </>
              )}
            </div>
            {showSocialMediaLinks && (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            )}
          </div>

          {/* CMS navigation blocks: EXPLORE, TRUST & SAFETY, COMPANY, LEGAL */}
          <div className={classNames(css.contentGrid, getGridCss(numberOfColumns))}>
            <BlockBuilder blocks={blocks} sectionId={sectionId} options={options} />
          </div>

        </div>

        {/* Bottom bar: copyright (rendered as markdown so CMS links work) */}
        <div className={css.footerBottom}>
          <div className={css.copyright}>
            <Field
              data={copyright?.content ? { ...copyright, fieldType: 'markdown' } : copyright}
            />
          </div>
        </div>

      </div>
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionFooter.defaultProps = {
  className: null,
  rootClassName: null,
  numberOfColumns: 1,
  socialMediaLinks: [],
  slogan: null,
  copyright: null,
  appearance: null,
  blocks: [],
  options: null,
};

SectionFooter.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  numberOfColumns: number,
  socialMediaLinks: arrayOf(object),
  slogan: object,
  copyright: object,
  appearance: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

export default SectionFooter;
